import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { APIError, APIErrorType, type CreateBenchmarkMutation, getAPIErrorMessage, queryUtils } from '@vizit/api';
import { useCreateBenchmarkMutation } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { BenchmarkFormSchema } from '@vizit/organisms';

const DEFAULT_ERROR_MESSAGE = 'Error creating benchmark. Please try again in a few minutes.';
const MISSING_WORKSPACE_MESSAGE = 'Workspace is still loading. Please try again in a moment.';

/** What the caller learns about a benchmark just created, besides its ID. */
interface CreatedBenchmark {
  name: string;
  scope: BenchmarkFormSchema['imageType'];
}

interface UseBenchmarkNewDialogProps {
  /** Workspace the benchmark is created in */
  workspaceId?: string;
  /** Called with the new benchmark's ID, name and image type once it is created */
  onSuccessfulCreate: (benchmarkId: string, benchmark: CreatedBenchmark) => void;
  /** Called when the API rejects the request as unauthenticated or unauthorized */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Submits the benchmark dialog: creates the benchmark, marks the workspace's
 * benchmark lists stale and reports back the new ID. Auth errors go to
 * `onAuthenticationError`; anything else is rethrown with a message for the
 * dialog to show. 5xx responses are already reported to Sentry by the API
 * client, so nothing is captured here.
 */
function useBenchmarkNewDialog(props: UseBenchmarkNewDialogProps) {
  const { workspaceId, onSuccessfulCreate, onAuthenticationError } = props;

  const queryClient = useQueryClient();

  const { mutateAsync: createBenchmark } = useCreateBenchmarkMutation();

  const onSubmit = async (data: BenchmarkFormSchema) => {
    if (!workspaceId) {
      Sentry.captureException(new Error('Workspace ID is required'), {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Benchmark,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkCreate,
        },
      });
      throw new Error(MISSING_WORKSPACE_MESSAGE);
    }

    let benchmark: CreateBenchmarkMutation;

    try {
      benchmark = await createBenchmark({
        workspace_id: workspaceId,
        name: data.name,
        description: data.description || null,
        scope: data.imageType,
      });
    } catch (error) {
      if (
        error instanceof APIError &&
        (error.type === APIErrorType.NotAuthenticated || error.type === APIErrorType.NotAuthorized)
      ) {
        onAuthenticationError(error);
        return;
      }

      throw new Error(getAPIErrorMessage(error, DEFAULT_ERROR_MESSAGE));
    }

    if (!benchmark?.id) {
      throw new Error(DEFAULT_ERROR_MESSAGE);
    }

    Sentry.addBreadcrumb({
      message: 'Benchmark created successfully.',
      level: 'info',
      data: { benchmark_id: benchmark.id, workspace_id: workspaceId, scope: data.imageType },
    });

    // Only mark the lists stale: the caller navigates away right after, so there
    // is nothing to refetch for yet.
    void queryUtils.invalidateAllBenchmarkLists(queryClient, workspaceId, { refetchType: 'none' });

    onSuccessfulCreate(benchmark.id, { name: data.name, scope: data.imageType });
  };

  return { onSubmit };
}

export { useBenchmarkNewDialog };
export type { CreatedBenchmark, UseBenchmarkNewDialogProps };
